import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    Grid,
    GridColumn,
    areObjectsEqual,
    ValidationResult,
    FormValidationSummary
} from "sprint-common-web";

import { ContentReference } from "../../../../common/types/ContentReference";
import { AssociateContentFieldsProps, AssociateContentFormFields } from "../components/AssociateContentFormFields";
import { useAssociateContentStandaloneForm } from "../hooks/useAssociateContentStandaloneForm";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";

interface AssociateContentStandaloneFormProps extends AssociateContentFieldsProps {
    onCancel: () => void;
    onSubmit: (contentReference?: ContentReference) => Promise<void>;
    onSubmitValidate?: (contentReference: ContentReference) => Promise<ValidationResult>;
}

export const AssociateContentStandaloneForm = (props: AssociateContentStandaloneFormProps): JSX.Element => {
    const {
        defaultContentState,
        isDefaultContentLoading,
        isEditDisabled,
        validation,
        infoMessage,
        formTitle,
        onCancel,
        onSubmit,
        onSubmitValidate
    } = props;

    const form = useForm<ContentAssociationFormState>({
        criteriaMode: "firstError",
        shouldUnregister: false,
        defaultValues: defaultContentState
    });

    const {
        mapFormStateToContentReference,
        setValidationResult,
        saveToHistory,
        validationResult
    } = useAssociateContentStandaloneForm();

    const handleSubmit = async (values: ContentAssociationFormState) => {
        const contentReference = await mapFormStateToContentReference(values);

        if (onSubmitValidate && contentReference) {
            const result = await onSubmitValidate(contentReference);
            setValidationResult(result);

            if (!result?.isValid) {
                return;
            }
        }

        await onSubmit(contentReference);
        await saveToHistory(values);
    };

    const values = form.watch();
    const hasChanges = !areObjectsEqual(values, defaultContentState);

    return (
        <FormProvider {...form}>
            <Grid>
                <GridColumn sm={3}>
                    <Form onSubmit={form.handleSubmit(handleSubmit)}>
                        <AssociateContentFormFields
                            formTitle={formTitle}
                            defaultContentState={defaultContentState}
                            isDefaultContentLoading={isDefaultContentLoading}
                            validation={validation}
                            isEditDisabled={isEditDisabled}
                            infoMessage={infoMessage}
                            style={{ minHeight: "80vh", minWidth: "80vh" }}
                        >
                            <FormValidationSummary validationResult={validationResult} />
                            <FormActions
                                onCancel={onCancel}
                                isLoading={form.formState.isSubmitting || form.formState.isValidating}
                                submitDisabled={!hasChanges}
                            />
                        </AssociateContentFormFields>
                    </Form>
                </GridColumn>
            </Grid>
        </FormProvider>
    );
};
